import React, { useState, useRef, useEffect } from "react";
import ProfileInfo from "./ProfileInfo";
import OptionComponent from "./ProfileOptions";
import {
  FaTelegramPlane,
  FaWhatsapp,
  FaInstagram,
  FaEnvelope,
} from "react-icons/fa";
import { IoLogoFacebook, IoLogoTwitter } from "react-icons/io";
import profileIcon from "./../../assets/images/profileIcon.svg";
import transactionIcon from "./../../assets/images/transactionIcon.svg";
import settingsIcon from "./../../assets/images/settings.svg";
import shareIcon from "./../../assets/images/share.svg";
import InfoIcon from "./../../assets/images/info.svg";
import brandsIcon from "./../../assets/images/brands-icon.svg";
import { useNavigate } from "react-router-dom";
import { UserDetails } from "../../models/UserDetails";
import { RootState } from "../../models/RootState";
import { connect, useDispatch } from "react-redux";
import logoutIcon from "../../assets/images/logout.svg";
import { disConnectToNotificationWS } from "../../websockets/webSocket";
import lockIcon from "../../assets/images/lock-icon.svg";
import { setIsLoggedIn, setExchangeAuthData, setSportBookAuthData } from "../../store/authReducer";
import { setIsCashoutEnabled, setTurboCashoutCommission } from "../../store/exchangeSports/exchangeSportsReducer";
import { getRefId } from "../../store/authActions";
import { SiteConfig } from "../../models/SiteConfig";
import { demoUser } from "../../utils/StringUtils";
import DemoDialog from "../DemoDialog";
import API from "../../services";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "../../hooks/useTranslation";

interface ProfileProps {
  userDetails: any;
  Browser: any
  siteConfig: SiteConfig;
  dynamicTextColors?: any;
}

const ProfilePage: React.FC<ProfileProps> = ({ userDetails, Browser, siteConfig, dynamicTextColors }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [mobDialog, setMobDialog] = useState(false);
  const [demoDialog, setDemoDialog] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [refUrl, setRefUrl] = useState("");
  const dispatch = useDispatch();

  // Get dynamic text color class from Redux instead of calling function
  const textColorClass = dynamicTextColors?.pageTextColorClass || '';

  // Helper function to check if user is created from back office
  // Format: {botId}-a... (e.g., 1844-anm777) vs regular: {botId}-{number}... (e.g., 1844-916281755762)
  const isBackOfficeUser = () => {
    const userKey = userDetails?.UserKey || sessionStorage.getItem("userKey");
    if (!userKey) return false;
    
    const parts = userKey.split("-");
    if (parts.length < 2) return false;
    
    // Check if the part after the dash starts with lowercase 'a'
    const afterDash = parts[1];
    return afterDash && /^a/.test(afterDash);
  };
  
  // Change password form states
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const menuOptions = [
    {
      icon: profileIcon,
      title: "My Profile",
      label: t("profile.myProfile"),
      description: t("profile.yourProfileAndPersonalInformation"),
    },
    {
      icon: transactionIcon,
      title: "Transaction History",
      label: t("profile.transactionHistory"),
      description: t("profile.yourTransactionHistory"),
    },
    {
      icon: settingsIcon,
      title: "Bet List",
      label: t("profile.betList"),
      description: t("profile.checkoutYourBets"),
    },
    {
      icon: settingsIcon,
      title: "Account Statement",
      label: t("profile.accountStatement"),
      description: t("profile.checkoutYourStatement"),
    },
    {
      icon: settingsIcon,
      title: "Site Account Statement",
      label: "Site Account Statement",
      description: "Check your site statement",
    },
    {
      icon: InfoIcon,
      title: "FAQ",
      label: t("profile.faq"),
      description: t("profile.newCheckThis"),
    },
    {
      icon: shareIcon,
      title: "Share",
      label: t("profile.share"),
      description: t("profile.shareYourReferralLink"),
    },
    {
      icon: lockIcon,
      title: "Change Password",
      label: t("profile.changePassword"),
      description: t("profile.changeYourAccountPassword"),
    },
    {
      icon: brandsIcon,
      title: "My Brands",
      label: "My Brands",
      description: "Create, view, and manage your brands",
    },
    {
      icon: logoutIcon,
      title: "Logout",
      label: t("profile.logout"),
      description: t("profile.logOutOfYourAccount"),
    },
  ];

  const shareDetails = {
    title: "BotYaPot",
    text: "This is the place to get all sports updates",
  };

  const transactionHistory = () => {
    navigate("/transactions");
  };

  const myProfile = () => {
    navigate("/myprofile");
  };

  const betList = () => {
    navigate("/betlist");
  }

  const accStatement = () => {
    navigate("/account-statement");
  }

  const siteAccStatement = () => {
    navigate("/site-account-statement");
  }

  const faq = () => {
    navigate("/faq");
  }

  const whitelabelSection = () => {
    navigate("/whitelabel");
  }

  const changePassword = () => {
    setChangePasswordDialog(true);
    // Reset form when opening
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
  }

  const handleCloseChangePasswordDialog = () => {
    setChangePasswordDialog(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
  }

  const validatePassword = (password: string) => {
    if (!password) return t("profile.messages.passwordIsRequired");

    const hasCapital = /[A-Z]/.test(password);
    const hasSmallLetter = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMinLength = password.length >= 8;

    if (!hasCapital || !hasSmallLetter || !hasNumber || !hasMinLength) {
      return t("profile.messages.passwordValidation");
    }

    return null; // Valid
  };

  const handlePasswordChange = async () => {
    // Reset error
    setPasswordError("");

    // Validate old password
    if (!oldPassword) {
      setPasswordError(t("profile.messages.oldPasswordRequired"));
      return;
    }

    // Validate new password
    const newPasswordError = validatePassword(newPassword);
    if (newPasswordError) {
      setPasswordError(newPasswordError);
      return;
    }

    // Check if new password and confirm password match
    if (newPassword !== confirmPassword) {
      setPasswordError(t("profile.messages.passwordsDoNotMatch"));
      return;
    }

    // Check if old and new passwords are different
    if (oldPassword === newPassword) {
      setPasswordError(t("profile.messages.newPasswordMustBeDifferent"));
      return;
    }

    setIsChangingPassword(true);
    try {
      const userId = userDetails?.UserKey || sessionStorage.getItem("userKey");
      
      const response = await API.post("/users/change-user-password", {
        userId: userId,
        oldPassword: oldPassword,
        newPassword: newPassword,
      });

      if (response.data?.status === "RS_OK") {
        toast.success(t("profile.messages.passwordChangedSuccessfully"));
        handleCloseChangePasswordDialog();
        // Logout user after successful password change
        setTimeout(() => {
          logoutHandler();
        }, 1000);
      } else {
        setPasswordError(response.data?.errorDescription || t("profile.messages.failedToChangePassword"));
      }
    } catch (error: any) {
      console.error("Error changing password:", error);
      setPasswordError(error?.response?.data?.errorDescription || t("profile.messages.errorOccurredPleaseTryAgain"));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const emptyAuthData = {
    providerId: "",
    token: "",
    operatorId: "",
    partnerId: "",
    currency: "",
    rate: 0,
  };

  const logoutHandler = () => {
    disConnectToNotificationWS();
    dispatch(setIsLoggedIn(false));
    dispatch(setIsCashoutEnabled(false));
    dispatch(setTurboCashoutCommission(0));
    dispatch(setExchangeAuthData(emptyAuthData));
    dispatch(setSportBookAuthData(emptyAuthData));
    sessionStorage.setItem("isLoggedIn", "false");
    sessionStorage.setItem("isDemo", "false");
    navigate("/login");
  }

  // const getRefId = async () => {
  //   let response;
  //   try {
  //     response = await API.post(`/users/get-user-details`, {
  //       userKey: sessionStorage.getItem("userKey"),
  //     });
  //   } catch (e) {
  //     console.log(e);
  //     return;
  //   }

  //   if (response.data.status !== "RS_OK") {
  //     toast.error("error sharing details");
  //     return;
  //   }

  //   if (response && response.data.userDetails.ReferralCode) {
  //     let code = response.data.userDetails.ReferralCode;
  //     let url = "https://t.me/yapot_bot?start=" + code;
  //     setRefUrl(url);
  //     if (navigator.share) {
  //       navigator
  //         .share({
  //           title: shareDetails.title,
  //           text: shareDetails.text,
  //           url: url,
  //         })
  //         .then(() => {
  //           console.log("Successfully shared");
  //         })
  //         .catch((error) => {
  //           console.error("Error sharing:", error);
  //           // setIsShareModalOpen(true);
  //         });
  //     } else {
  //       setIsShareModalOpen(true);
  //     }
  //   }
  // };

  const handleShare = () => {
    // Check if user is a demo user
    if (demoUser()) {
      setDemoDialog(true);
      return;
    }

    // console.log("userDetails,",userDetails)
    let code = userDetails?.ReferralCode;
    // let url = "https://t.me/yapot_bot?start=" + code;

    let url = userDetails?.telegramLink + "?start=" + code
    if (Browser === "Web") {

      
    const encodedUrl = window.location.href;
    const pageUrl = decodeURIComponent(encodedUrl);
    const ur = new URL(pageUrl);

    url = ur.origin + "/login?referral=" + code

      // url = "https://stage-web.strike46.com/login?referral=" + code
    }
    setRefUrl(url);

    if (isBackOfficeUser() || userDetails?.MobileNumber || siteConfig?.isContactOptional) {
      if (navigator.share) {
        navigator
          .share({
            title: shareDetails.title,
            text: shareDetails.text,
            url: url,
          })
          .then(() => {
            console.log("Successfully shared");
          })
          .catch((error) => {
            console.error("Error sharing:", error);
            // setIsShareModalOpen(true);
          });
      } else {
        setIsShareModalOpen(true);
      }
    }
    else {
      setMobDialog(true)
    }
  };

  function requestUserContact() {
    try {
      window?.Telegram?.WebApp?.ready();
      window?.Telegram?.WebApp?.requestContact((contact) => {
        if (contact) {
          console.log("Contact received:", contact);
          dispatch(getRefId() as any);
        } else {
          console.log("User declined to share contact.");
        }
      });
    } catch (e) {
      console.log("xc", e)
    }
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsShareModalOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let code = userDetails?.ReferralCode;

    let url = userDetails?.telegramLink + "?start=" + code
    if (Browser === "Web") {      
    const encodedUrl = window.location.href;
    const pageUrl = decodeURIComponent(encodedUrl);
    const ur = new URL(pageUrl);

    url = ur.origin + "/login?referral=" + code
    }
    setRefUrl(url);
  }, []);

  return (
    <>
      <div
        className={`flex flex-col mx-auto w-full min-h-[calc(100vh-4rem)] mt-[4rem] ${textColorClass}`}
      >
        <div className="flex flex-col items-center w-full">
          <ProfileInfo textColorClass={textColorClass} />
          {menuOptions
            .filter(option => 
              (option.title !== "Logout" || Browser !== "TG") &&
              (option.title !== "Change Password" || (Browser === "Web" && !demoUser())) &&
              (option.title !== "Share" || !siteConfig?.blockReferrals) &&
              (option.title !== "My Brands" || userDetails?.allowWhitelabel === true)
            )
            .map((option, index) => (
              <OptionComponent
                key={index}
                icon={option.icon}
                title={option.title}
                label={option.label}
                description={option.description}
                refUrl={option.title === "Share" ? refUrl : undefined}
                userDetails={userDetails}
                siteConfig={siteConfig}
                setMobDialog={setMobDialog}
                setDemoDialog={setDemoDialog}
                isBackOfficeUser={isBackOfficeUser}
                requestUserContact={requestUserContact}
                textColorClass={textColorClass}
                onClick={
                  option.title === "Share"
                    ? handleShare
                    : option.title === "Transaction History"
                      ? transactionHistory
                      : option.title === "My Profile"
                        ? myProfile
                        : option.title === "Bet List"
                          ? betList
                          : option.title === "Account Statement"
                            ? accStatement
                            : option.title === "Site Account Statement"
                              ? siteAccStatement
                            : option.title === "FAQ"
                              ? faq
                            : option.title === "My Brands"
                                ? whitelabelSection
                              : option.title === "Change Password"
                                ? changePassword
                                : option.title === "Logout"
                                  ? logoutHandler
                                  : undefined
                }
              />
            ))}

        </div>
        <div className="h-[100px]"></div>
      </div>

      {mobDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className={`bg-inputbox-color p-6 rounded-lg w-full max-w-md ${textColorClass}`}>
            <h3 className={`text-lg mb-4 text-center ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>{t("profile.shareYourContact")}</h3>
            <p className={`text-center ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
              {t("profile.toReferYourFriends")}
            </p>
            <div className="mt-4 flex gap-4">
              <button
                className={`w-1/2 bg-meicnbg-color px-4 py-2 rounded hover:bg-gray-700 ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                onClick={() => setMobDialog(false)}
              >
                {t("common.close")}
              </button>
              <button
                onClick={requestUserContact}
                className={`w-1/2 bg-meicnbg-color px-4 py-2 rounded hover:bg-gray-700 ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
              >
                {t("profile.share")}
              </button>
            </div>
          </div>
        </div>
      )}

      <DemoDialog
        isOpen={demoDialog}
        onClose={() => setDemoDialog(false)}
      />

      {changePasswordDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-inputbox-color p-6 rounded-lg w-full max-w-md relative ${textColorClass}`}>
            <CloseIcon
              className={`absolute top-3 right-3 cursor-pointer ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
              onClick={handleCloseChangePasswordDialog}
            />
            <h3 className={`text-lg mb-4 text-center ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>{t("profile.changePassword")}</h3>
            
            {/* Old Password */}
            <div className="mb-4">
              <label className={`text-sm mb-1 block ${textColorClass === 'dark-text' ? 'text-black' : 'text-zinc-400'}`}>
                {t("profile.oldPassword")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showOldPassword ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder={t("profile.enterOldPassword")}
                  className={`w-full p-2 pr-10 rounded bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-primary text-sm ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className={`absolute inset-y-0 right-2 flex items-center ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                >
                  {showOldPassword ? <Visibility sx={{ fontSize: 16 }} /> : <VisibilityOff sx={{ fontSize: 16 }} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="mb-4">
              <label className={`text-sm mb-1 block ${textColorClass === 'dark-text' ? 'text-black' : 'text-zinc-400'}`}>
                {t("profile.newPassword")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    // Real-time validation: check if passwords match
                    if (confirmPassword && e.target.value && e.target.value !== confirmPassword) {
                      setPasswordError(t("profile.messages.passwordsDoNotMatch"));
                    } else if (confirmPassword && e.target.value && e.target.value === confirmPassword) {
                      setPasswordError("");
                    } else if (passwordError && passwordError.includes("match")) {
                      setPasswordError("");
                    }
                  }}
                  placeholder={t("profile.enterNewPassword")}
                  className={`w-full p-2 pr-10 rounded bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-primary text-sm ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className={`absolute inset-y-0 right-2 flex items-center ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                >
                  {showNewPassword ? <Visibility sx={{ fontSize: 16 }} /> : <VisibilityOff sx={{ fontSize: 16 }} />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div className="mb-4">
              <label className={`text-sm mb-1 block ${textColorClass === 'dark-text' ? 'text-black' : 'text-zinc-400'}`}>
                {t("profile.confirmNewPassword")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    // Real-time validation: check if passwords match
                    if (newPassword && e.target.value && newPassword !== e.target.value) {
                      setPasswordError(t("profile.messages.passwordsDoNotMatch"));
                    } else if (newPassword && e.target.value && newPassword === e.target.value) {
                      setPasswordError("");
                    } else if (passwordError && passwordError.includes("match")) {
                      setPasswordError("");
                    }
                  }}
                  placeholder={t("profile.confirmNewPasswordPlaceholder")}
                  className={`w-full p-2 pr-10 rounded bg-zinc-800 border focus:outline-none focus:ring-2 focus:ring-primary text-sm ${
                    passwordError && newPassword !== confirmPassword ? 'border-red-500' : 'border-zinc-700'
                  } ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={`absolute inset-y-0 right-2 flex items-center ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                >
                  {showConfirmPassword ? <Visibility sx={{ fontSize: 16 }} /> : <VisibilityOff sx={{ fontSize: 16 }} />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {passwordError && (
              <div className="mb-4">
                <p className="text-red-500 text-xs">{passwordError}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleCloseChangePasswordDialog}
                disabled={isChangingPassword}
                className={`w-1/2 bg-zinc-700 px-4 py-2 rounded hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handlePasswordChange}
                disabled={isChangingPassword || !oldPassword || !newPassword || !confirmPassword}
                className={`w-1/2 bg-meicnbg-color px-4 py-2 rounded hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
              >
                {isChangingPassword ? t("profile.changing") : t("profile.change")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div
            ref={modalRef}
            className={`bg-inputbox-color p-6 rounded-lg w-full max-w-md ${textColorClass}`}
          >
            <h3 className={`text-lg mb-4 text-center ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
              {t("profile.shareOptions")}
            </h3>
            <div className="flex justify-around">
              <button
                className="p-2 hover:text-blue-500"
                onClick={() =>
                  window.open(
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                      shareDetails.text
                    )}&url=${encodeURIComponent(refUrl)}`,
                    "_blank"
                  )
                }
              >
                <IoLogoTwitter size={24} color="#1DA1F2" /> {/* Twitter Blue */}
              </button>
              <button
                className="p-2 hover:text-blue-700"
                onClick={() =>
                  window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      refUrl
                    )}`,
                    "_blank"
                  )
                }
              >
                <IoLogoFacebook size={24} color="#4267B2" />{" "}
                {/* Facebook Blue */}
              </button>
              <button
                className="p-2 hover:text-green-500"
                onClick={() =>
                  window.open(
                    `whatsapp://send?text=${encodeURIComponent(
                      shareDetails.text
                    )}%20${encodeURIComponent(refUrl)}`,
                    "_blank"
                  )
                }
              >
                <FaWhatsapp size={24} color="#25D366" /> {/* WhatsApp Green */}
              </button>
              <button
                className="p-2 hover:text-blue-400"
                onClick={() =>
                  window.open(
                    `tg://msg_url?url=${encodeURIComponent(
                      refUrl
                    )}&text=${encodeURIComponent(shareDetails.text)}`,
                    "_blank"
                  )
                }
              >
                <FaTelegramPlane size={24} color="#0088cc" />{" "}
                {/* Telegram Blue */}
              </button>
              <button
                className="p-2 hover:text-gray-300"
                onClick={() =>
                  window.open(
                    `mailto:?subject=${encodeURIComponent(
                      shareDetails.title
                    )}&body=${encodeURIComponent(refUrl)}`,
                    "_blank"
                  )
                }
              >
                <FaEnvelope size={24} color="#D44638" /> {/* Mail Red */}
              </button>
              {/* <button
                className="p-2 hover:text-pink-400"
                onClick={() =>
                  window.open(
                    `https://www.instagram.com/share?url=${encodeURIComponent(
                      refUrl
                    )}`,
                    "_blank"
                  )
                }
              >
                <FaInstagram size={24} color="#C13584" />
              </button> */}
            </div>
            <button
              className={`mt-4 w-full bg-meicnbg-color px-4 py-2 rounded hover:bg-gray-700 ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
              onClick={() => setIsShareModalOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
    Browser: state.userDetails?.Browser,
    siteConfig: state.userDetails?.siteConfig,
    dynamicTextColors: state.userDetails?.dynamicTextColors,
  };
};

export default connect(mapStateToProps)(ProfilePage);
